/**
 * The state of the "Vilken princip?" set (pedagogy spec §7.3.5, E5), kept in `sessionStorage` under
 * its own key so a refresh survives and starting it never replaces an unfinished practice session.
 * Pure data helpers; the page is `WhichPrinciplePage.tsx`.
 *
 * Each pick is posted when it is made (`POST /api/practice/plan`), so leaving mid-set keeps the
 * evidence; this state only remembers where the student is and what they picked.
 */
import type { Localized, PublicQuestion } from "@shared/types";
import type { PlanOffer } from "../types";

const STORAGE_KEY = "mf.practice.planSet";

export interface PlanSetData {
  startedAt: number;
  /** How many problems the link asked for: fewer served means the pool was short (says so). */
  requested: number;
  questions: PublicQuestion[];
  /** One offer per question: the set never serves an item without a plan step. */
  plans: Record<string, PlanOffer>;
  index: number;
  /** Question id → the anchor picked; set when the pick is made and kept even if saving fails. */
  picks: Record<string, string>;
  /**
   * Question ids whose pick has not reached the server: set with the pick, cleared once it is
   * saved. A failed pick stays here (the student has seen the answer, so it is not taken back)
   * until "Försök igen" sends the same pick; a page that loads one offers the retry. Absent
   * when empty, so sets stored before this field load unchanged.
   */
  unsaved?: Record<string, true>;
  /** The exam session the suggestion came from (`origin.examSessionId`, provenance only). */
  from?: string;
}

export function createPlanSet(
  questions: PublicQuestion[],
  plans: Record<string, PlanOffer>,
  requested: number,
  from?: string,
): PlanSetData {
  // An item without an offer cannot be played; the server never sends one.
  const playable = questions.filter((question) => question.id in plans);
  return {
    startedAt: Date.now(),
    requested,
    questions: playable,
    plans: Object.fromEntries(playable.map((question) => [question.id, plans[question.id]!])),
    index: 0,
    picks: {},
    ...(from ? { from } : {}),
  };
}

/** True when the stored value has the shape the page reads; anything else is dropped on load. */
function isPlanSet(value: unknown): value is PlanSetData {
  if (typeof value !== "object" || value === null) return false;
  const data = value as Record<string, unknown>;
  return (
    Array.isArray(data.questions) &&
    typeof data.plans === "object" &&
    data.plans !== null &&
    typeof data.picks === "object" &&
    data.picks !== null &&
    typeof data.index === "number" &&
    typeof data.requested === "number"
  );
}

/** The set with the pick on `questionId` made and not yet saved (the optimistic step). */
export function withUnsavedPick(
  data: PlanSetData,
  questionId: string,
  topicId: string,
): PlanSetData {
  return {
    ...data,
    picks: { ...data.picks, [questionId]: topicId },
    unsaved: { ...data.unsaved, [questionId]: true },
  };
}

/** The set once the pick on `questionId` has been saved; `unsaved` goes away when none is left. */
export function withSavedPick(data: PlanSetData, questionId: string): PlanSetData {
  if (!data.unsaved?.[questionId]) return data;
  const { unsaved, ...rest } = data;
  const { [questionId]: _saved, ...left } = unsaved;
  return Object.keys(left).length > 0 ? { ...rest, unsaved: left } : rest;
}

export function savePlanSet(data: PlanSetData): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* storage full or unavailable (private mode): nothing is kept, so `WhichPrinciplePage`, which
       reads the set with `loadPlanSet`, finds none and sends the student back to /practice */
  }
}

export function loadPlanSet(): PlanSetData | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isPlanSet(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearPlanSet(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** A principle the student mixed up, with what the chip said about it. */
export interface MissedPrinciple {
  topicId: string;
  name: Localized;
  cue: Localized;
}

export interface PlanSetResult {
  /** Problems picked on, right or wrong. */
  total: number;
  right: number;
  /** The right principles of the wrong picks, once each, in the order they came up. */
  missed: MissedPrinciple[];
}

/** The summary's numbers: "{right} av {total} principer rätt" and the principles to practise. */
export function planSetResult(data: PlanSetData): PlanSetResult {
  let total = 0;
  let right = 0;
  const missed: MissedPrinciple[] = [];
  for (const question of data.questions) {
    const pick = data.picks[question.id];
    const offer = data.plans[question.id];
    if (pick === undefined || !offer) continue;
    total += 1;
    if (pick === offer.targetTopicId) {
      right += 1;
      continue;
    }
    const target = offer.options.find((option) => option.topicId === offer.targetTopicId);
    if (target && !missed.some((entry) => entry.topicId === target.topicId)) {
      missed.push({ topicId: target.topicId, name: target.name, cue: target.cue });
    }
  }
  return { total, right, missed };
}
